<template>
  <aside class="summary">
    <section class="summary__top">
      <div class="summary__top-content">
        <p v-if="slots?.slot_type_billing === 'Postpago'">Costo total del mes:</p>
        <p v-else>Próximo pago:</p>
        <h1>${{ totalIncludeIgv }}</h1>
        <p>Vencimiento: {{ formatDate() }}</p>
      </div>
      <img src="../../assets/payment-card.png" alt="card icon" />
    </section>
    <section class="summary__middle">
      <h3>Resumen:</h3>
      <p v-if="slots?.slot_type_billing === 'Postpago'">
        Número de slots: {{ slots?.slots_invoice }}
      </p>
      <p v-if="slots?.slot_type_billing === 'Prepago'">Número de slots: {{ props.totalSlots }}</p>
      
      <div class="d-flex2">
        <p>Precio por slot: ${{ slots?.slot_price }}</p>
        <a-tooltip v-if="slots?.tax" placement="bottom">
          <template #title>
            Es el precio por cada slot. El IGV no se incluye, se calcula aparte.
          </template>
          <InfoCircleOutlined />
        </a-tooltip>
      </div>

      <div class="d-flex2" v-if="slots?.tax">
        <p>IGV (18%) de slots: ${{ igvSlot }}</p>
        <a-tooltip placement="bottom">
          <template #title>
            Cálculo del 18% de IGV sobre el precio por slot multiplicado por el número de slots. Es un costo adicional separado.
          </template>
          <InfoCircleOutlined />
        </a-tooltip>
      </div>
      
      <p v-if="hasSummary">Fee {{ CompanyName }} GPT: ${{ iaPrice }}</p>
    </section>
    <section class="summary__button">
      <PayUPaymentButton
        :amount="totalIncludeIgv"
        :disabled="isButtonDisabled"
        @success="successPay"
        name="Pagar"
        :type="ProductType.Recharge"
      />
    </section>
    <a-divider class="divider" />
    <section class="summary__bottom">
      <h3>💡Información de Pago</h3>
      <p class="info__text">
        Para evitar cargos adicionales en tu próximo ciclo de facturación, asegúrate de eliminar los
        slots que ya no necesitas antes de tu fecha de facturación.
      </p>
    </section>
  </aside>
</template>

<script setup lang="ts">
import { computed, toRefs, ref } from "vue";
import dayjs from "dayjs";
import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import { ISlots } from "@/@types/settings/slots";
import { ProductType } from "@/@types/settings/billing";
import { CompanyName } from "@/app/shared/utils/constants";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

const igvFactor = 1.18;
const props = defineProps<{
  slots: ISlots | undefined;
  totalSlots: number;
  iaPrice: number;
  hasSummary: boolean;
}>();

const { slots } = toRefs(props);
const emit = defineEmits(["successPay"]);

const monthlyPrice = computed(() => {
  let slotTotal = 0;
  if (slots.value?.slot_type_billing === 'Postpago') {
    slotTotal = slots.value?.slots_invoice || 0;
  } else {
    slotTotal = props.totalSlots || 0;
  }
  // const slotTotal = slots.value?.slots_invoice || 0;
  const slotPrice = slots.value?.slot_price || 0;

  if (isNaN(slotTotal) || isNaN(slotPrice)) {
    return 0;
  }
  return slotTotal * slotPrice;
});

const totalIncludeIgv = computed(() => {
  const totalIncludeIgvValue = monthlyPrice.value * (slots?.value?.tax ? igvFactor : 1);
  return Number(totalIncludeIgvValue.toFixed(2));
});

const igvSlot = computed(() => {
  const igvSlotValue = monthlyPrice.value * slots?.value?.tax;
  return Number(igvSlotValue.toFixed(2));
})

const currentDate = ref(dayjs());

const billingDate = computed(() => dayjs(slots.value?.date_payment));
const dueDate = computed(() => dayjs(slots.value?.date_payment_finish));
const billingBeforeThreeDays = computed(() => billingDate.value.subtract(3, "days"));
const nextDueDate = computed(() => dueDate.value.add(1, "days"));

const isButtonDisabled = computed(() => {
  if (slots.value?.slot_paid) return true;

  const isPrepaid = slots.value?.slot_type_billing === "Prepago";
  const isButtonEnabled =
    !(currentDate.value.isBefore(billingBeforeThreeDays.value) ||
    currentDate.value.isAfter(nextDueDate.value));

  // return isPrepaid ? isButtonEnabled : false;
  return isPrepaid ? !isButtonEnabled : true;
});

const formatDate = () => {
  const parseDate = dayjs(slots.value?.date_payment_finish);
  return parseDate.format("DD/MM/YYYY");
};

const successPay = () => {
  emit("successPay");
};

defineExpose({ monthlyPrice });
</script>

<style lang="sass" scoped>
h1, h3, p
    margin: 0
h1
    font-size: 24px
h3
    color: $gray-9
    font-size: $font-normal
    font-weight: 600
p
    color: $gray-13
    font-size: 14px
.divider
    background-color: $gray-5
    margin: 16px 0
.summary
    width: 100%
    height: 100%
    border: 1px solid $gray-5
    border-radius: 8px
    padding: 16px
    overflow: auto
    &__top
        display: flex
        justify-content: space-between
        margin-bottom: 4px
        &-content
            p:last-child
                font-size: $font-small
                color: $gray-7
    &__middle
        display: flex
        flex-direction: column
        gap: 5px
        padding: 5px 0 10px 0
        p
          color: $gray-8
    &__bottom
        h3
            font-size: $font-medium
.d-flex2
    display: flex
    gap: 5px
    align-items: center
</style>
